import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { ModalController, ToastController } from '@ionic/angular/standalone';
import { compareVersions } from '@shared/utils';
import { environment } from 'src/environments/environment';

import { ForceUpdateComponent } from '../component/force-update/force-update.component';
import { BrowserService } from '../core/services/browser/browser.service';
import { ApiService } from './api.service';
import { apiBehind } from './api-contract';

/** `app_update` de GET app-settings (ver AppVersionPolicy del API). */
export interface AppUpdatePolicy {
  min_version_ios?: string | null;
  min_version_android?: string | null;
  update_url_ios?: string | null;
  update_url_android?: string | null;
  message?: string | null;
  /** Mínimo guardado con "Forzar": no hay excepción para quien su tienda aún no lo ofrece. */
  strict?: boolean | null;
}

export interface ForceUpdateInfo {
  message: string | null;
  updateUrl: string | null;
  /** Versión mínima de esta plataforma; sin ella no se puede preguntar a la tienda. */
  minVersion?: string | null;
  strict?: boolean;
}

/** Header con el que el app avisa que su tienda aún no ofrece el mínimo (EnforceMinAppVersion). */
export const STORE_BEHIND_HEADER = 'X-App-Update-Unavailable';

/**
 * Actualización obligatoria (auditoría de seguridad 2026-10). El servidor fija una versión mínima por
 * plataforma en Ajustes; por debajo de ella el app muestra una pantalla que no se puede cerrar y
 * sólo lleva a la tienda. Dos señales:
 * - al arrancar y al volver a primer plano se lee `app_update` de app-settings (endpoint público);
 * - cualquier llamada que el servidor rechace con 426 (`force_update: true`) abre la misma pantalla
 *   en vez de cerrar la sesión: al actualizar, el usuario sigue dentro.
 *
 * Antes de bloquear se le pregunta a la tienda si ya ofrece el mínimo. Si contesta que no (versión aún
 * sin publicar, o un lanzamiento escalonado de Google Play que no ha llegado a este usuario), bloquear
 * lo dejaría sin salida: el app sigue funcionando, avisa una vez y manda STORE_BEHIND_HEADER para que
 * el servidor no responda 426. Si el mínimo es `strict` ("Forzar" en Ajustes) o la tienda no contesta
 * (sin red, build que no se instaló desde la tienda), se bloquea como siempre.
 *
 * TEC-296: la misma lectura de app-settings trae el contrato del API (`api.contract`). Si la instancia
 * va detrás de lo que este app necesita (MIN_API_CONTRACT), la pantalla dice "Estamos actualizando el
 * servicio" con "Reintentar", y se quita sola cuando la instancia ya cumple (al reintentar o al volver
 * a primer plano). Un API sin `api` cuenta como contrato 1: con el mínimo en 1 nunca aparece.
 */
@Injectable({
  providedIn: 'root',
})
export class ForceUpdateService {
  private api = inject(ApiService);
  private modalCtrl = inject(ModalController);
  private browser = inject(BrowserService);
  private toastCtrl = inject(ToastController);
  private modal: HTMLIonModalElement | null = null;
  /** "Estamos actualizando el servicio": la instancia va detrás del app (TEC-296). */
  private serviceModal: HTMLIonModalElement | null = null;
  /** Sólo se deja cerrar cuando la instancia ya cumple el contrato. */
  private serviceRecovered = false;
  private opening = false;
  private noticeShown = false;

  /** La tienda confirmó que aún no ofrece el mínimo: las llamadas al API llevan STORE_BEHIND_HEADER. */
  storeBehind = false;

  /** IDs de la ficha de esta build en las tiendas (los mismos del recordatorio de actualización). */
  readonly appStoreOptions = {
    appId: '1577332718',
    androidPackageName: 'app.technit.movilidadtec',
  };

  async check(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const res = await this.api.getData('app-settings');
    // TEC-296: con la instancia detrás del app no tiene caso pedir actualizar el app; primero el servicio.
    if (apiBehind(res?.api)) {
      await this.blockServiceUpdating();
      return;
    }
    await this.releaseServiceUpdating();
    const info = ForceUpdateService.evaluate(res?.app_update, Capacitor.getPlatform(), environment.version);
    if (info) {
      await this.enforce(info);
    } else if (res?.app_update) {
      this.storeBehind = false; // ya no hay mínimo que cumplir (o ya se cumple)
    }
  }

  /**
   * Por debajo del mínimo: bloquea, salvo que la tienda confirme que todavía no lo ofrece y el mínimo
   * no sea estricto. Devuelve 'continue' si el app puede seguir (y reintentar con el header).
   */
  async enforce(info: ForceUpdateInfo): Promise<'blocked' | 'continue'> {
    if (!info.strict && info.minVersion && (await this.storeOffers(info.minVersion, info.updateUrl)) === false) {
      this.storeBehind = true;
      await this.noticeOnce();
      return 'continue';
    }
    this.storeBehind = false;
    await this.block(info);
    return 'blocked';
  }

  /**
   * ¿La tienda le ofrece a este usuario la versión mínima? null = no se sabe.
   * - iOS: la versión publicada en la App Store (búsqueda pública de Apple).
   * - Android: Google Play sólo da el versionCode, así que se usa su disponibilidad para este usuario:
   *   "sin actualización" = nada más nuevo para él (sin publicar o fuera del lanzamiento escalonado).
   *   Si hay una más nueva que aún no llega al mínimo, se bloquea; tras instalarla, ya no habrá nada
   *   más nuevo y el app sigue.
   */
  async storeOffers(minVersion: string, updateUrl: string | null): Promise<boolean | null> {
    try {
      if (Capacitor.getPlatform() === 'ios') {
        const country = /apps\.apple\.com\/([a-z]{2})\//i.exec(updateUrl ?? '')?.[1]?.toLowerCase() ?? 'mx';
        const info = await AppUpdate.getAppUpdateInfo({ country });
        return info.availableVersionName ? compareVersions(info.availableVersionName, minVersion) >= 0 : null;
      }
      const info = await AppUpdate.getAppUpdateInfo();
      switch (info.updateAvailability) {
        case AppUpdateAvailability.UPDATE_NOT_AVAILABLE:
          return false;
        case AppUpdateAvailability.UPDATE_AVAILABLE:
        case AppUpdateAvailability.UPDATE_IN_PROGRESS:
          return true;
        default:
          return null;
      }
    } catch (error) {
      console.error('ForceUpdateService: getAppUpdateInfo failed', error);
      return null;
    }
  }

  private async noticeOnce(): Promise<void> {
    if (this.noticeShown) {
      return;
    }
    this.noticeShown = true;
    const toast = await this.toastCtrl.create({
      message: 'Hay una actualización obligatoria que todavía no llega a tu tienda. Instálala en cuanto aparezca.',
      duration: 6000,
      position: 'top',
      buttons: [{ text: 'OK', role: 'cancel' }],
    });
    await toast.present();
  }

  /** ¿Esta versión queda por debajo del mínimo de su plataforma? Pura, para las pruebas. */
  static evaluate(policy: AppUpdatePolicy | null | undefined, platform: string, version: string): ForceUpdateInfo | null {
    if (!policy || (platform !== 'ios' && platform !== 'android')) {
      return null;
    }
    const minimum = platform === 'ios' ? policy.min_version_ios : policy.min_version_android;
    if (!minimum || compareVersions(version, minimum) >= 0) {
      return null;
    }

    return {
      message: policy.message ?? null,
      updateUrl: (platform === 'ios' ? policy.update_url_ios : policy.update_url_android) ?? null,
      minVersion: minimum,
      strict: policy.strict === true,
    };
  }

  /** Abre la pantalla de actualización (una sola vez; no se puede cerrar). */
  async block(info: ForceUpdateInfo): Promise<void> {
    if (this.modal || this.opening) {
      return;
    }
    this.opening = true;
    try {
      this.modal = await this.modalCtrl.create({
        component: ForceUpdateComponent,
        componentProps: { message: info.message, onUpdate: () => void this.openStore(info.updateUrl) },
        backdropDismiss: false,
        canDismiss: false,
        cssClass: 'force-update-modal',
      });
      await this.modal.present();
    } finally {
      this.opening = false;
    }
  }

  /** La instancia va detrás de lo que este app necesita: pantalla sin cerrar, con "Reintentar". */
  private async blockServiceUpdating(): Promise<void> {
    if (this.serviceModal || this.opening) {
      return;
    }
    this.opening = true;
    this.serviceRecovered = false;
    try {
      this.serviceModal = await this.modalCtrl.create({
        component: ForceUpdateComponent,
        componentProps: {
          title: 'Estamos actualizando el servicio',
          message: 'En unos minutos podrás seguir usando la app. Vuelve a intentarlo en un momento.',
          buttonLabel: 'Reintentar',
          icon: 'refresh-outline',
          onUpdate: () => void this.check(),
        },
        backdropDismiss: false,
        // canDismiss también frena dismiss(): sólo se suelta cuando la instancia ya cumple.
        canDismiss: async () => this.serviceRecovered,
        cssClass: 'force-update-modal',
      });
      await this.serviceModal.present();
    } finally {
      this.opening = false;
    }
  }

  /** La instancia ya cumple el contrato: quita la pantalla si estaba abierta. */
  private async releaseServiceUpdating(): Promise<void> {
    const modal = this.serviceModal;
    if (!modal) {
      return;
    }
    this.serviceRecovered = true;
    this.serviceModal = null;
    await modal.dismiss();
  }

  /** La ficha de la tienda de esta build; si el plugin falla, la URL que manda el servidor. */
  async openStore(updateUrl: string | null): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      try {
        await AppUpdate.openAppStore(this.appStoreOptions);
        return;
      } catch (error) {
        console.error('ForceUpdateService: openAppStore failed', error);
      }
    }
    if (updateUrl) {
      this.browser.openExternal(updateUrl);
    }
  }
}
